import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import moment from 'moment';
import { strings } from '../resources/strings';
import ServicesContext from './ServicesContext';
import { Session } from '../models/Session';
import { LocationZerezes } from '../models/LocationZerezes';
import { useDispatch } from 'react-redux';
import { MainActions } from '../actions/MainActions';
import { SessionHelper } from '../helpers/SessionHelper';

interface IChannel {
  necessaryMinutes?: number|null;
  remainingMinutes: number;
  message: {
    content: string;
    type: 'question' | 'alert';
  } | null | undefined
}

interface IAuthContext {
  session?: Session | null;
  userLocations: number[];
  detailedUserLocations?: LocationZerezes[];
  mainLocation?: LocationZerezes | null;
  signIn(login?: string, password?: string, callback?: (success: boolean, message: string) => void): void;
  signOut(callback?: () => void): void;
  getChannel(minutes?: number): Promise<IChannel>;
}

const AuthContext = createContext<IAuthContext>({} as IAuthContext);

const AuthProvider: React.FC = ({ children }) => {

  const { authService, storageService } = useContext(ServicesContext);
  const dispatch = useDispatch();
  const mainActions = React.useMemo(() => new MainActions(dispatch), [dispatch]);

  const [session, setSession] = useState<Session | null>(() => {
    return storageService.get<Session>(strings.StorageSessionKey);
  });
  const intervalRef = useRef<any>(null);

  const getUserLocations = (locations?: string) => locations?.split(',').map(lid => +lid.trim()) || [];
  const getMainLocation = (firstLocationId?: number | null, locations?: LocationZerezes[]) =>
    locations?.length
      ? locations?.find(l => l.id === firstLocationId) || null
      : null;

  const [userLocations, setUserLocations] = useState<number[]>(getUserLocations(session?.user?.locations));
  const [detailedUserLocations, setDetailedUserLocations] = useState<LocationZerezes[]>(session?.locations || []);
  const [mainLocation, setMainLocation] = useState<LocationZerezes | undefined | null>(
    getMainLocation(session?.user?.locations?.length ? getUserLocations(session?.user?.locations)[0] : null, session?.locations));

  const signIn = (login: string, password: string, callback?: (success: boolean, message: string) => void) => {
    authService.authenticate(login, password)
      .then(result => {
        if (intervalRef.current) {
          console.log('clear timer:', intervalRef.current);
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        if (result) result.date = new Date();
        storageService.set(strings.StorageSessionKey, result);
        console.log('setting session...');
        setSession(result);
        const locations = result?.locations || [];
        const locationsId = getUserLocations(result?.user?.locations);
        setUserLocations(locationsId);
        setDetailedUserLocations(locations || []);
        const firstLocationId = locationsId.length > 0 ? locationsId[0] : null;
        setMainLocation(getMainLocation(firstLocationId, locations));
        mainActions?.setSessionIsExpiring(false);
        intervalRef.current = setInterval(checkSession, 5000);
        console.log('new timer:', intervalRef.current);
        if (callback) callback(true, '');
      })
      .catch(error => {
        if (callback) callback(false, typeof error === 'string' ? error : strings.OpsSomethingWrongDuringAuthentication);
      });
  };

  const signOut = (callback?: () => void) => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    mainActions.setSessionIsExpiring(false);
    storageService.set(strings.StorageSessionKey, null);
    setSession(null);
    setUserLocations([]);
    setDetailedUserLocations([]);
    setMainLocation(null);
    if (callback && typeof callback === 'function') callback();
  };

  useEffect(() => {
    handleVisibilityChange();
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return (
    <AuthContext.Provider value={{ session, signIn, signOut, userLocations, detailedUserLocations, mainLocation, getChannel }}>
      {children}
    </AuthContext.Provider>
  );

  function handleVisibilityChange() {
    if (document.visibilityState === 'visible') {
      checkSession();
      if (intervalRef.current) {
        console.log('clear timer:', intervalRef.current);
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      intervalRef.current = setInterval(checkSession, 5000);
      console.log('new timer:', intervalRef.current);
      return;
    }
    if (intervalRef.current) {
      console.log('clear timer:', intervalRef.current);
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }

  function getChannel(minutes?: number): Promise<IChannel> {
    return new Promise((resolve, reject) => {
      const currentSession = storageService.get<Session>(strings.StorageSessionKey);
      if (!currentSession) {
        resolve({
          necessaryMinutes: minutes,
          remainingMinutes: 0,
          message: {
            content: 'A sessão expirou. Realize um novo login.',
            type: 'alert'
          }
        });
        return;
      }
      const stateExpiration = SessionHelper.getStateExpiration(currentSession);
      if (stateExpiration.expired) {
        resolve({
          necessaryMinutes: minutes,
          remainingMinutes: 0,
          message: {
            content: 'A sessão expirou. Realize um novo login.',
            type: 'alert'
          }
        });
        return;
      }
      if (!minutes) {
        resolve({
          necessaryMinutes: minutes,
          remainingMinutes: stateExpiration.remainingMinutes,
          message: {
            content: 'A sessão vai expirar em ' + stateExpiration.remainingMinutes + ' minutos. Deseja continuar?',
            type: 'question'
          }
        });
        return;
      }
      if (stateExpiration.remainingMinutes < minutes) {
        resolve({
          necessaryMinutes: minutes,
          remainingMinutes: 0,
          message: {
            content: 'A sessão não possui tempo suficiente para a operação. Realize um novo login.',
            type: 'alert'
          }
        });
        return;
      }
      resolve({
        necessaryMinutes: minutes,
        remainingMinutes: stateExpiration.remainingMinutes,
        message: null
      });
    });
  }

  function checkSession() {
    const currentSession = storageService.get<Session>(strings.StorageSessionKey);
    if (!currentSession || !mainActions) {
      console.log(`checking ... session:${currentSession ? 'ok['+moment(currentSession.date).format('DD/MM/yyyy HH:mm:ss')+']' : 'null'} | actions:${mainActions?'ok':null} | timer:${intervalRef.current}`);
      if (mainActions) {
        mainActions.setSessionIsExpiring(false);
      }
      return;
    }
    try {
      console.log(`checking ... session:${currentSession ? 'ok['+moment(currentSession.date).format('DD/MM/yyyy HH:mm:ss')+']' : 'null'} | actions:${mainActions?'ok':null} | timer:${intervalRef.current}`);
      const sessionState = SessionHelper.getStateExpiration(currentSession);
      if (sessionState.isExpiring) {
        console.log(`session is expiring: ${sessionState.isExpiringBecause}`);
        mainActions.setSessionIsExpiring(true);
      } else {
        mainActions.setSessionIsExpiring(false);
      }
    } catch (err) {
      console.log('check session error:', err);
      mainActions.setSessionIsExpiring(false);
    }
  }
};

const useAuth = (): IAuthContext => {
  const context = useContext(AuthContext);
  return context;
};

export { AuthProvider, useAuth };